import { inject, Injectable } from '@angular/core';
import { Content } from '@context/shared/types/common';
import { ContentFolder } from '@context/shared/types/folder';
import { ExternalViewContextService } from '@context/frontend/user';
import { DocumentReference, getDoc } from 'firebase/firestore';

/**
 * Centralizes the routing/navigation rules for the external viewer.
 *
 * Whatever content the share link initially resolves to is treated as the "root"
 * of the viewer's navigation stack. The viewer can descend into nested folders and
 * files but is never allowed to navigate above that root, mirroring the access the
 * Firestore security rules grant the share session.
 */
@Injectable({ providedIn: 'root' })
export class ExternalViewerNavService {
	private readonly context = inject(ExternalViewContextService);

	/** The shared content id that anchors the navigation stack. */
	get rootId(): string | null {
		return this.context.data()?.contentId ?? null;
	}

	get externalId(): string | null {
		return this.context.data()?.externalId ?? null;
	}

	isRoot(content: Pick<Content, 'id'>): boolean {
		return !!this.rootId && content.id === this.rootId;
	}

	/** Router link to a piece of content within the current share. */
	contentLink(content: Pick<Content, 'id' | 'type'>): unknown[] {
		return ['/content', this.externalId, content.type, content.id];
	}

	/** Router link to a folder within the current share, by id. */
	folderLink(id: string): unknown[] {
		return ['/content', this.externalId, 'folder', id];
	}

	/**
	 * Returns the parent folder id to navigate back to, or `null` when the content
	 * is the share root (the top of the viewer's navigation stack).
	 */
	parentWithinBounds(content: Content): string | null {
		if (this.isRoot(content)) return null;
		return content.parent?.id ?? null;
	}

	/**
	 * Builds the breadcrumb trail for a folder, bounded to the share root.
	 *
	 * Only path segments at or below the root are read - segments above the root
	 * are both irrelevant to the viewer and unreadable under the share session's
	 * security rules, so they are never fetched.
	 */
	async boundedBreadcrumbs(folder: ContentFolder): Promise<ContentFolder[]> {
		const rootId = this.rootId;
		if (!rootId || folder.id === rootId) return [folder];

		const path = (folder.path ?? []) as DocumentReference<ContentFolder>[];
		const rootIndex = path.findIndex((segment) => segment.id === rootId);
		if (rootIndex < 0) return [folder];

		const boundedRefs = path.slice(rootIndex);
		const crumbs = await Promise.all(
			boundedRefs.map((ref) =>
				getDoc(ref).then(
					(snapshot) => snapshot.data() as ContentFolder,
				),
			),
		);

		return [...crumbs, folder];
	}
}
